import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { UserPlusIcon, ExclamationCircleIcon, MagnifyingGlassIcon, BuildingOfficeIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ClientList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showWithoutCodeOnly, setShowWithoutCodeOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchClients = async (searchQuery = '') => {
    setLoading(true);
    setError(null);

    try {
      // By default, only show active clients
      // If showInactive is true, include inactive clients
      // If showArchived is true, include archived clients
      const activeOnly = !showInactive && !showArchived;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('active_only', activeOnly);
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      const response = await api.get(`/clients?${params.toString()}`);
      console.log('Clients response:', response.data);
      
      // Handle different response formats
      let clientsData = [];
      if (Array.isArray(response.data)) {
        clientsData = response.data;
      } else if (response.data && Array.isArray(response.data.clients)) {
        clientsData = response.data.clients;
      } else if (response.data && typeof response.data === 'object') {
        console.warn('Unexpected clients response format:', response.data);
        clientsData = [];
      }
      
      // Apply filters based on checkbox settings
      if (showArchived) {
        // Only show archived clients
        clientsData = clientsData.filter(client => client.is_archived);
      } else if (showInactive) {
        // Only show inactive (but not archived) clients
        clientsData = clientsData.filter(client => !client.is_active && !client.is_archived);
      } else {
        // Default behavior: show only active (and not archived) clients
        clientsData = clientsData.filter(client => client.is_active && !client.is_archived);
      }
      
      setClients(clientsData);
      setFilteredClients(clientsData);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again later.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [showInactive, showArchived]);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setSearchLoading(true);
      }
      fetchClients(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, showInactive, showArchived]);

  const handleRowClick = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  // Check if any filters are active
  const hasActiveFilters = showInactive || showArchived || showWithoutCodeOnly || searchTerm.trim();

  // Clear all filters
  const clearFilters = () => {
    setShowInactive(false);
    setShowArchived(false);
    setShowWithoutCodeOnly(false);
    setSearchTerm('');
  };

  // Apply "code not set" filter client-side (no refetch needed)
  const displayClients = showWithoutCodeOnly
    ? filteredClients.filter(c => !c.client_code || c.client_code === '')
    : filteredClients;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Clients</h1>
          <p className="mt-2 text-sm text-gray-700">
            A list of all clients in your account
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Link
            to="/clients/add"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Client
          </Link>
        </div>
      </div>

      {/* Filter Section - Enhanced with Indigo Accents */}
      <div className={`mt-6 mb-6 bg-white p-4 rounded-lg shadow-sm border transition-colors duration-200 ${
        hasActiveFilters ? 'border-indigo-200 ring-1 ring-indigo-100' : 'border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                searchTerm.trim() ? 'text-indigo-500' : 'text-gray-400'
              }`} />
              <input
                type="text"
                className={`block w-full pl-10 pr-10 py-2 border rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 sm:text-sm transition-colors duration-200 ${
                  searchTerm.trim() 
                    ? 'border-indigo-300 focus:ring-indigo-500 focus:border-indigo-500' 
                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
                placeholder="Search by client name, client code, contact name, or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchLoading && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className={`ml-2 text-sm transition-colors duration-200 ${
                showInactive ? 'text-indigo-700 font-medium' : 'text-gray-700'
              }`}>Show Inactive</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className={`ml-2 text-sm transition-colors duration-200 ${
                showArchived ? 'text-indigo-700 font-medium' : 'text-gray-700'
              }`}>Show Archived</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showWithoutCodeOnly}
                onChange={(e) => setShowWithoutCodeOnly(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className={`ml-2 text-sm transition-colors duration-200 ${
                showWithoutCodeOnly ? 'text-indigo-700 font-medium' : 'text-gray-700'
              }`}>Code not set</span>
            </label>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-3 py-2 border border-indigo-300 shadow-sm text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex items-center text-sm text-indigo-600">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
              <span>
                {searchTerm.trim() && `Search: "${searchTerm}"`}
                {searchTerm.trim() && (showInactive || showArchived || showWithoutCodeOnly) && ' • '}
                {showInactive && 'Including Inactive'}
                {showInactive && showArchived && ' • '}
                {showArchived && 'Including Archived'}
                {(showInactive || showArchived) && showWithoutCodeOnly && ' • '}
                {showWithoutCodeOnly && 'Code not set only'}
              </span>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                {displayClients.length === 0 ? (
                  <div className="text-center py-12 bg-white">
                    <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No clients found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating a new client.'}
                    </p>
                    {!searchTerm && (
                      <div className="mt-6">
                        <Link
                          to="/clients/add"
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                          Add Client
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client Code
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          DBA
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Projects
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Products
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayClients.map((client) => (
                        <tr 
                          key={client.id}
                          onClick={() => handleRowClick(client.id)}
                          className="bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="font-medium text-gray-900">
                              {client.name || 'Unnamed Client'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {client.client_code ? (
                              <span className="font-mono text-sm bg-indigo-50 text-indigo-700 px-2 py-1 rounded border border-indigo-200">
                                {client.client_code}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs italic">
                                Not set
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[200px] truncate" title={client.dba || ''}>
                            {client.dba ? client.dba : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {client.is_archived ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Archived
                              </span>
                            ) : !client.is_active ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Inactive
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {client.created_at ? new Date(client.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                              {client.projects ? client.projects.length : 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                              {client.products_count || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList; 